import { NavLink, useNavigate } from "react-router-dom";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { canAccess } from "@/lib/roles";

interface NavItem {
  label: string;
  icon: string;
  path: string;
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

const navGroups: NavGroup[] = [
  {
    label: "Overview",
    items: [{ label: "Dashboard", icon: "ri-dashboard-line", path: "/admin/dashboard" }],
  },
  {
    label: "Broadcast",
    items: [
      { label: "Live TV", icon: "ri-live-line", path: "/admin/live" },
      { label: "L+TV Direct", icon: "ri-broadcast-line", path: "/admin/direct" },
      { label: "TV Schedule", icon: "ri-calendar-check-line", path: "/admin/schedule" },
      { label: "Breaking News", icon: "ri-flashlight-line", path: "/admin/breaking" },
    ],
  },
  {
    label: "Content",
    items: [
      { label: "Newsroom", icon: "ri-newspaper-line", path: "/admin/news" },
      { label: "Videos", icon: "ri-movie-line", path: "/admin/videos" },
      { label: "Programs", icon: "ri-tv-line", path: "/admin/programs" },
      { label: "Gallery", icon: "ri-image-line", path: "/admin/gallery" },
      { label: "Media Library", icon: "ri-folder-line", path: "/admin/media" },
      { label: "Homepage", icon: "ri-home-line", path: "/admin/homepage" },
      { label: "Pages", icon: "ri-file-text-line", path: "/admin/pages" },
    ],
  },
  {
    label: "Sections",
    items: [
      { label: "Entertainment & Culture", icon: "ri-music-2-line", path: "/admin/entertainment" },
      { label: "Sports", icon: "ri-basketball-line", path: "/admin/sports" },
      { label: "Religion & Community", icon: "ri-heart-line", path: "/admin/religion" },
      { label: "Photo Studio", icon: "ri-camera-line", path: "/admin/photo-studio" },
      { label: "Production", icon: "ri-video-line", path: "/admin/production" },
    ],
  },
  {
    label: "Engagement",
    items: [
      { label: "Social Media", icon: "ri-share-line", path: "/admin/social" },
      { label: "Partners", icon: "ri-group-line", path: "/admin/partners" },
      { label: "Advertising", icon: "ri-megaphone-line", path: "/admin/advertising" },
      { label: "Adverts", icon: "ri-megaphone-line", path: "/admin/adverts" },
      { label: "Careers", icon: "ri-briefcase-line", path: "/admin/careers" },
      { label: "Newsletter", icon: "ri-mail-send-line", path: "/admin/newsletter" },
      { label: "Email Broadcast", icon: "ri-send-plane-line", path: "/admin/broadcast" },
      { label: "Program Reminders", icon: "ri-alarm-line", path: "/admin/reminders" },
      { label: "Messages", icon: "ri-message-line", path: "/admin/messages" },
      { label: "Analytics", icon: "ri-bar-chart-line", path: "/admin/analytics" },
      { label: "SEO", icon: "ri-search-line", path: "/admin/seo" },
    ],
  },
  {
    label: "System",
    items: [
      { label: "Users & Roles", icon: "ri-team-line", path: "/admin/users" },
      { label: "Security", icon: "ri-shield-check-line", path: "/admin/security" },
      { label: "Activity Logs", icon: "ri-history-line", path: "/admin/activity" },
      { label: "System Status", icon: "ri-pulse-line", path: "/admin/system" },
      { label: "Website Settings", icon: "ri-settings-3-line", path: "/admin/settings" },
      { label: "Help", icon: "ri-question-line", path: "/admin/help" },
    ],
  },
];

export default function Sidebar({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { user, logout } = useAdminAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/admin/login");
  };

  return (
    <>
      {open && (
        <div
          className="fixed inset-0 z-30 bg-foreground-900/40 lg:hidden"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      <aside
        className={`fixed inset-y-0 left-0 z-40 w-64 bg-background-950 text-background-100 flex flex-col transition-transform duration-300 lg:translate-x-0 ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between px-5 h-16 border-b border-background-800">
          <div className="flex items-center gap-2">
            <span className="w-9 h-9 flex items-center justify-center rounded-md bg-accent-500 text-white font-extrabold text-sm">
              L+
            </span>
            <div className="leading-tight">
              <p className="font-bold text-sm tracking-wide">L+TV</p>
              <p className="text-[10px] uppercase tracking-widest text-background-400">
                Administration
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="lg:hidden w-8 h-8 flex items-center justify-center rounded-md hover:bg-background-800 text-background-300"
            aria-label="Close menu"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-5">
          {navGroups.map((group) => {
            const visibleItems = group.items.filter((item) =>
              canAccess(user?.role ?? null, item.path),
            );
            if (visibleItems.length === 0) return null;
            return (
              <div key={group.label}>
                <p className="px-3 mb-2 text-[10px] font-semibold uppercase tracking-widest text-background-400">
                  {group.label}
                </p>
                <ul className="space-y-0.5">
                  {visibleItems.map((item) => (
                    <li key={item.path}>
                      <NavLink
                        to={item.path}
                        onClick={onClose}
                        className={({ isActive }) =>
                          `flex items-center gap-3 px-3 py-2 rounded-md text-sm whitespace-nowrap transition-colors ${
                            isActive
                              ? "bg-accent-500 text-white font-semibold"
                              : "text-background-200 hover:bg-background-800 hover:text-white"
                          }`
                        }
                      >
                        <span className="w-5 h-5 flex items-center justify-center">
                          <i className={`${item.icon} text-base`} />
                        </span>
                        {item.label}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </nav>

        <div className="p-4 border-t border-background-800">
          <div className="flex items-center gap-3 px-2 mb-3">
            <span className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-500 text-white text-xs font-bold">
              {user?.initials ?? "L+"}
            </span>
            <div className="min-w-0">
              <p className="text-sm font-semibold truncate">{user?.name ?? "Administrator"}</p>
              <p className="text-[11px] text-background-400 truncate">{user?.role ?? "—"}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm text-background-200 hover:bg-background-800 hover:text-white transition-colors"
          >
            <span className="w-5 h-5 flex items-center justify-center">
              <i className="ri-logout-box-r-line text-base" />
            </span>
            Logout
          </button>
        </div>
      </aside>
    </>
  );
}